import { Vue, Component, Prop } from 'vue-property-decorator';
import { VNodeData, VNode, CreateElement } from 'vue';
import { clx, capitalize } from '../../lib';

type Classes = {
  root?: any;
  badge?: any;
  anchorOriginTopRightRectangle?: any;
  anchorOriginBottomRightRectangle?: any;
  anchorOriginTopLeftRectangle?: any;
  anchorOriginBottomLeftRectangle?: any;
  anchorOriginTopRightCircle?: any;
  anchorOriginBottomRightCircle?: any;
  anchorOriginTopLeftCircle?: any;
  anchorOriginBottomLeftCircle?: any;
  invisible?: any;
  [key: string]: any;
};

type Size = 'small' | 'medium' | 'large';

type Color =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'warning'
  | 'success';

type AnchorOrigin = {
  vertical: 'top' | 'bottom';
  horizontal: 'left' | 'right';
};

type Overlap = 'circle' | 'rectangle';

type Variant = 'standard' | 'dot';

@Component
export default class ClBadge extends Vue {
  @Prop({
    default: () => ({
      vertical: 'top',
      horizontal: 'right'
    }),
    type: Object as () => AnchorOrigin
  })
  readonly anchorOrigin!: AnchorOrigin;

  @Prop({
    default: '',
    type: [String, Number]
  })
  readonly badgeContent!: string | number;

  @Prop({
    default: () => ({
      root: '',
      badge: '',
      invisble: '',
      anchorOriginTopRightRectangle: '',
      anchorOriginBottomRightRectangle: '',
      anchorOriginTopLeftRectangle: '',
      anchorOriginBottomLeftRectangle: '',
      anchorOriginTopRightCircle: '',
      anchorOriginBottomRightCircle: '',
      anchorOriginTopLeftCircle: '',
      anchorOriginBottomLeftCircle: ''
    }),
    type: Object as () => Classes
  })
  readonly classes!: Classes;

  @Prop({
    default: 'default',
    type: String as () => Color,
    validator: x =>
      [
        'default',
        'primary',
        'secondary',
        'error',
        'warning',
        'success'
      ].includes(x)
  })
  readonly color!: Color;

  @Prop({
    default: 'span',
    type: String
  })
  readonly component!: string;

  @Prop({
    default: false,
    type: Boolean
  })
  readonly invisible!: boolean;

  @Prop({
    default: 99,
    type: Number
  })
  readonly max!: number;

  @Prop({
    default: 'rectangle',
    type: String as () => Overlap,
    validator: x => ['circle', 'rectangle'].includes(x)
  })
  readonly overlap!: Overlap;

  @Prop({
    type: String as () => Size,
    validator: x => ['small', 'medium', 'large'].includes(x),
    default: 'medium'
  })
  readonly size!: Size;

  @Prop({
    type: String as () => Variant,
    default: 'standard',
    validator: x => ['standard', 'dot'].includes(x)
  })
  readonly variant!: Variant;

  render(h: CreateElement): VNode {
    const Component = this.component;

    let displayValue: string | number = '';

    if (this.variant !== 'dot') {
      displayValue =
        this.badgeContent > this.max ? `${this.max}+` : this.badgeContent;
    }

    return (
      <Component class={['cl-badge', this.classes.root]}>
        {this.$slots.default}
        <span
          class={clx(
            {
              'cl-badge__badge': true,
              [`cl-badge__badge--color-${this.color}`]:
                this.color !== 'default',
              [`cl-badge__badge--size-${this.size}`]: true,
              [`cl-badge__badge--overlap-${this.overlap}`]: true,
              [`cl-badge__badge--anchor-origin-${this.anchorOrigin.vertical}-${this.anchorOrigin.horizontal}-${this.overlap}`]: true,
              [`cl-badge__badge--variant-dot`]: this.variant === 'dot',
              'cl-badge__badge--invisible': this.invisible
            },
            this.classes.badge,
            this.classes[
              `anchorOrigin${capitalize(
                this.anchorOrigin.vertical
              )}${capitalize(this.anchorOrigin.horizontal)}${capitalize(
                this.overlap
              )}`
            ],
            this.invisible && this.classes.invisible
          )}
        >
          {displayValue}
        </span>
      </Component>
    );
  }
}
